import React from "react";
import styled from "styled-components";
import { useMediaQuery } from "react-responsive";
import { Tooltip } from "react-tooltip";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import { sumImpsCpi, numberWithCommas } from "modules/functions";
import ToggleSwitch from "components/forms/ToggleSwitch";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import SponsoredMessage from "components/posts/SponsoredMessage";

import eyeIcon from "assets/img/icons/eye-icon.svg";
import fingerIcon from "assets/img/icons/finger-icon.svg";
import percentIcon from "assets/img/icons/percent-icon.svg";
import pencilIcon from "assets/img/icons/pencil-icon.svg";
import pencilWhiteIcon from "assets/img/icons/pencil-icon-white.svg";
import binIcon from "assets/img/icons/bin-icon.svg";
import binWhiteIcon from "assets/img/icons/bin-icon-white.svg";

const SponsoredMessageBox = styled.div`
  width: 100%;
  height: ${(props) => (props.dashlist ? "100%" : "auto")};
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;
  max-width: ${(props) => (props.dashlist ? "450px" : "750px")};

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  transition: 0.2s;

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const BoxHeader = styled.div`
  width: 100%;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const StatsWrap = styled.div``;

const StatusBox = styled.div`
  padding-top: 6px;

  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding-top: 2px;
  }
`;

const StatusControl = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusBadge = styled.div`
  height: 20px;
  padding: 0 14px 1px;

  font-size: 13px;
  color: ${(props) => (props.status === "paused" ? css.DARK_BLUE : "#fff")};
  white-space: nowrap;

  border-radius: 10px;
  background: ${(props) => {
    switch (props.status) {
      case "inReview":
        return css.BRAND_INDIGO;
      case "active":
        if (!props.overlimit) return css.BRAND_GREEN;
        else return css.NEGATIVE_RED;
      case "paused":
        return css.BRAND_BLUSH;
      case "stopped":
        return css.NEGATIVE_RED;
      default:
        return css.TEXT_GREY;
    }
  }};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const ControlButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BoxContent = styled.div`
  display: flex;
  gap: ${css.VERTICAL_BOX_MARGIN};
  align-self: flex-start;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

const StatsBox = styled.div`
  padding: ${css.BOX_PADDING_TERTIARY};
  max-width: 150px;
  min-width: 150px;
  box-sizing: border-box;

  background: #3c556c;
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  flex: 0.5;

  display: grid;
  grid-template-columns: 64px 1fr;
  grid-template-rows: 25px 1fr 1fr 1fr;
`;

const BoxTitleCell = styled.div`
  display: flex;
`;

const BoxTitleIcon = styled.div`
  width: 16px;
  min-width: 16px;
  height: 16px;
`;

const StatsLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const LabelIcon = styled.div`
  width: 16px;
  min-width: 16px;
  height: 16px;
`;

const ValueCell = styled.div`
  height: 24px;

  font-size: 13px;
`;

const ChangeBadge = styled.div`
  height: 16px;
  padding: 0 4px 1px;
  margin-left: 5px;

  font-size: 11px;

  border-radius: 8px;
  background: ${(props) =>
    props.green ? css.POSITIVE_GREEN : css.NEGATIVE_RED};

  display: flex;
  justify-content: center;
  align-items: center;
`;

const PreviewBox = styled.div``;

const ControlsComponent = ({
  messageId,
  onEditClick,
  onDeleteClick,
  loadingDelete,
}) => {
  return (
    <ControlButtons>
      <ButtonRoundIcon
        icon={pencilIcon}
        hoverIcon={pencilWhiteIcon}
        background={css.BRAND_YELLOW}
        hoverBackground={css.BRAND_GREEN}
        onClick={() => onEditClick(messageId)}
        buttonSize="34px"
        iconSize="15px"
      />
      <ButtonRoundIcon
        icon={binIcon}
        hoverIcon={binWhiteIcon}
        background={css.BRAND_YELLOW}
        hoverBackground={css.NEGATIVE_RED}
        onClick={() => onDeleteClick(messageId)}
        buttonSize="34px"
        iconSize="15px"
        disabled={loadingDelete}
      />
    </ControlButtons>
  );
};

const LabelIconWithTip = ({ icon, tip }) => {
  return (
    <LabelIcon>
      <img
        src={icon}
        style={{ width: "100%" }}
        data-tooltip-id={`${icon}-tip`}
        data-tooltip-content={tip}
        data-tooltip-place="top"
        data-tooltip-delay-show={500}
      />
      <Tooltip id={`${icon}-tip`} className="tooltip" />
    </LabelIcon>
  );
};

export default ({
  message,
  onSwitchClick,
  switchLoading,
  onEditClick,
  onDeleteClick,
  loadingDelete,
  profileData,
}) => {
  const {
    id,
    status,
    text,
    link_text,
    link,
    switched_on,
    impressions,
    sponsored_messages_stats,
    rejection_reason,
  } = message;

  const statsTo = sponsored_messages_stats[0]
    ? sponsored_messages_stats[0].to
    : null;
  const statsTotalImpressions = sponsored_messages_stats[0]
    ? sponsored_messages_stats[0].total_impressions
    : null;
  const statsTotalClick = sponsored_messages_stats[0]
    ? sponsored_messages_stats[0].total_clicks
    : null;

  let statusText;

  if (status === "inReview") {
    statusText = "In Review";
  } else if (status === "active") {
    statusText = "Active";
  } else if (status === "paused") {
    statusText = "Paused";
  }

  // Calculate Impressions, Interactions, CTR, CPI and CPC stats
  const dayLimit = new Date();
  dayLimit.setDate(dayLimit.getDate() - 1);

  const twoDaysLimit = new Date();
  twoDaysLimit.setDate(twoDaysLimit.getDate() - 2);

  const weekLimit = new Date();
  weekLimit.setDate(weekLimit.getDate() - 7);

  const last24hoursImps = impressions.filter(
    (imp) => imp.created_at >= dayLimit.toISOString()
  );
  const last24hoursInts = last24hoursImps.filter((imp) => imp.clicked);

  const previous24hoursImps = impressions.filter(
    (imp) =>
      imp.created_at < dayLimit.toISOString() &&
      imp.created_at >= twoDaysLimit.toISOString()
  );
  const previous24hoursInts = previous24hoursImps.filter((imp) => imp.clicked);

  const last24hoursImpsChange =
    previous24hoursImps.length > 0
      ? (100 / previous24hoursImps.length) * last24hoursImps.length - 100
      : "-";

  const last24hoursIntsChange =
    previous24hoursInts.length > 0
      ? (100 / previous24hoursInts.length) * last24hoursInts.length - 100
      : "-";

  const last24hoursCTR =
    last24hoursImps.length > 0
      ? Math.round(
          (100 / last24hoursImps.length) * last24hoursInts.length * 10
        ) / 10
      : 0;

  const lastWeekImps = impressions.filter(
    (imp) => imp.created_at >= weekLimit.toISOString()
  );
  const lastWeekInts = lastWeekImps.filter((imp) => imp.clicked);

  const previousWeekImps = impressions.filter(
    (imp) => imp.created_at < weekLimit.toISOString()
  );

  const lastWeekCTR =
    lastWeekImps.length > 0
      ? Math.round((100 / lastWeekImps.length) * lastWeekInts.length * 10) / 10
      : 0;

  const impsFromLastTotalUpdate = statsTo
    ? impressions.filter((imp) => imp.created_at > statsTo)
    : impressions;

  const totalImpressions =
    statsTotalImpressions + impsFromLastTotalUpdate.length;

  const intsFromLastTotalUpdate = impsFromLastTotalUpdate.filter(
    (imp) => imp.clicked
  );

  const totalClicks = statsTotalClick + intsFromLastTotalUpdate.length;

  const totalCTR =
    totalImpressions > 0
      ? Math.round((100 / totalImpressions) * totalClicks * 10) / 10
      : 0;

  const reduceValue = (value) => {
    if (value < 10000) return numberWithCommas(value);
    return Math.round(value / 1000, 0) + "k";
  };

  return (
    <SponsoredMessageBox key={id}>
      <BoxHeader>
        <StatusBox>
          {!rejection_reason && (
            <StatusControl>
              <ToggleSwitch
                on={switched_on}
                onClick={() => onSwitchClick(id)}
                loading={switchLoading.includes(id)}
              />
              <StatusBadge status={status}>{statusText}</StatusBadge>
            </StatusControl>
          )}
          {rejection_reason && (
            <StatusBadge status="Stopped">Rejected</StatusBadge>
          )}
          <Tooltip id="limitTip" className="tooltip" />
        </StatusBox>
        <ControlsComponent
          messageId={id}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
          loadingDelete={loadingDelete}
        />
      </BoxHeader>
      <BoxContent>
        <PreviewBox>
          <SponsoredMessage
            profileImage={profileData.profileImage}
            username={profileData.username}
            checkmark={profileData.adPlan}
            text={text}
            linkText={link_text}
            link={link}
            listBox={true}
          />
        </PreviewBox>
        <StatsWrap>
          <StatsBox>
            <BoxTitleCell>
              <BoxTitleIcon>
                <img
                  src={eyeIcon}
                  style={{ width: "100%" }}
                  data-tooltip-id="impressions"
                  data-tooltip-content="Impressions"
                  data-tooltip-place="top"
                  data-tooltip-delay-show={500}
                />
                <Tooltip id="impressions" className="tooltip" />
              </BoxTitleIcon>
            </BoxTitleCell>
            <BoxTitleCell></BoxTitleCell>
            <StatsLabel>24 Hours</StatsLabel>
            <ValueCell>{numberWithCommas(last24hoursImps.length)}</ValueCell>
            <StatsLabel>7 Days</StatsLabel>
            <ValueCell>{numberWithCommas(lastWeekImps.length)}</ValueCell>
            <StatsLabel>Total</StatsLabel>
            <ValueCell>{reduceValue(totalImpressions)}</ValueCell>
          </StatsBox>
        </StatsWrap>
      </BoxContent>
    </SponsoredMessageBox>
  );
};
